import { Market } from '../components/ui/MarketSelector'

export type ActionItem = { title: string; status: 'Blocking' | 'To action'; category: string }

export type PathStep = {
  title: string
  status: string
  statusTone: 'amber' | 'red' | 'grey' | 'green'
  body: string
  note?: string
  citation?: string
  citationUrl?: string
}

export type OverviewConfig = {
  classification: string
  riskTag: string
  riskTone?: 'danger' | 'warning'
  riskTooltip: string
  timingPill: string
  timingTone?: 'warning'
  classificationNote?: string
  categoryPill: string
  pathwayNote?: string
  whyThisClassification: string
  whatToExpect: string
  pathToMarket?: { subtitle: string; steps: PathStep[]; pendingCitations?: string[] }
  marketRow: { label: string; risk: string; riskTone?: 'warning'; riskTooltip?: string; completeness: number; required: string; missing: string }
  taama: { blocking: string; toAction: string; summary: string; readiness?: string }
  findings: {
    formula: { detail: string; count: string }
    claims?: { detail: string; count: string }
    label: { title: string; detail: string; count?: string; notAssessed?: boolean }
  }
  needs: { count: string; items: ActionItem[]; showMore: string }
  recentlyUpdated: { type: string; filename: string; date: string }[]
}

export type ProductRecordConfig = {
  recordName: string
  readiness: number
  pills: { category: string; markets: string; documents: string; date: string }
  market: Market
  hasNip: boolean
  showNip?: boolean
  supportsCompare?: boolean
  overview: OverviewConfig
  sgReadiness?: number
  sgOverview?: OverviewConfig
}

const orangeRecord: ProductRecordConfig = {
  recordName: 'Orange Immunity Powder',
  readiness: 26,
  pills: { category: 'supplements', markets: '2 markets', documents: '2 documents', date: '26 Jul 2026' },
  market: { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA' },
  hasNip: false,
  overview: {
    classification: 'Listed Complementary Medicine (AUST L)',
    riskTag: 'Not listable as formulated',
    riskTone: 'danger',
    riskTooltip: 'Two ingredient restrictions block listing against this pathway',
    timingPill: 'Intended pathway — blocked by two ingredient restrictions',
    categoryPill: 'Route: oral · Powder',
    pathwayNote:
      'The intended pathway is available, but this formulation cannot be listed against it. Two ingredient restrictions must be resolved first.',
    whyThisClassification:
      'Orange Immunity Powder is a vitamin and mineral supplement in single-serve sachet form making general health-maintenance claims, which fits the definition of a Listed Complementary Medicine (AUST L). It is classified here rather than as a conventional food because it is presented and marketed as a therapeutic supplement.',
    whatToExpect:
      'Fast, self-managed pathway — the sponsor self-certifies and the TGA performs an administrative check only. The current artwork is a United States label and requires substantial rework before listing: it carries no AUST L number, no Australian sponsor, no batch number, no expiry date, and no advisory statements. Vitamin A is present above the level at which an advisory statement is required.',
    pathToMarket: {
      subtitle: 'How Orange Immunity Powder reaches shelf in Australia.',
      steps: [
        {
          title: 'Resolve two formulation blockers',
          status: 'Blocked',
          statusTone: 'red',
          body:
            'This formulation cannot be listed as an AUST L medicine as currently formulated. Flavour concentration sits at roughly six times the permitted level, and vitamin A exceeds the maximum daily dose. Both have specific numeric targets — see Formula tab, Blocker section.',
          citation: 'Therapeutic Goods (Permissible Ingredients) Determination',
          citationUrl:
            'https://www.tga.gov.au/products/regulations-all-products/ingredients-and-scheduling-medicines-and-chemicals/permissible-ingredients-determination',
        },
        {
          title: 'Self-list on the Australian Register of Therapeutic Goods (ARTG)',
          status: 'Not started',
          statusTone: 'grey',
          body:
            'Once the formulation clears, this is a fast, self-managed pathway — the sponsor self-certifies compliance and the TGA performs an administrative check only. An AUST L number can usually be obtained as soon as listing data is entered.',
          note: 'The real effort sits in assembling quality, GMP and claim-evidence documents alongside the listing itself, not the listing step.',
        },
      ],
    },
    marketRow: { label: 'Australia (TGA)', risk: 'Low risk', completeness: 17, required: 'Required 2/12', missing: '10 missing' },
    taama: {
      blocking: '12 blocking',
      toAction: '6 to action',
      readiness: '22',
      summary:
        'This formulation cannot be listed as an AUST L medicine. Two ingredient restrictions block it — flavour concentration at roughly six times the permitted level, and vitamin A above the maximum daily dose.',
    },
    findings: {
      formula: { detail: '5 permitted · 2 restricted · 5 unresolved', count: '12' },
      label: { title: 'Packaging & Label', detail: '25 fail · 19 pass · 2 inconclusive', count: '25' },
    },
    needs: {
      count: '16',
      items: [
        { title: 'Vitamin A exceeds advisory statement threshold', status: 'Blocking', category: 'Labelling' },
        { title: 'AUST L number absent from label', status: 'Blocking', category: 'Labelling' },
        { title: 'Australian sponsor name and address absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Batch/lot number and expiry date absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Actives declared in United States Supplement Facts format', status: 'Blocking', category: 'Labelling' },
      ],
      showMore: 'Show 11 more',
    },
    recentlyUpdated: [
      { type: 'Product Formulation', filename: 'Orange_Immunity_Powder_Formulation.xlsx', date: '26 Jul' },
      { type: 'Label Artwork', filename: 'Orange_15ct_Carton_R4.pdf', date: '26 Jul' },
    ],
  },
  sgReadiness: 58,
  sgOverview: {
    classification: 'Health Supplement',
    riskTag: 'Low risk',
    riskTooltip: 'Pathway risk — health supplements are not licensed pre-market in Singapore',
    timingPill: 'No pre-market licensing — supply once documentation is in order',
    categoryPill: 'Route: oral · Powder',
    whyThisClassification:
      'Orange Immunity Powder is presented as a dietary supplement providing vitamins and a mineral in single-serve sachet form. Under the Singapore Health Supplement Guidelines it is a health supplement, regulated by the Health Sciences Authority.',
    whatToExpect:
      'Health supplements are not subject to pre-marketing licensing in Singapore — there is no application and no registration number. The obligations are compositional and on-label. The current artwork is missing three mandatory particulars and carries food-style wording inappropriate for a health supplement.',
    pathToMarket: {
      subtitle: 'How Orange Immunity Powder reaches shelf in Singapore.',
      steps: [
        {
          title: "Resolve vitamin A dose to Singapore's tighter cap",
          status: 'Blocked',
          statusTone: 'red',
          body:
            "HSA caps vitamin A at 1.5 mg (5,000 IU / 1,500 µg RE) per day for general adult health supplements — half Australia's ceiling. This formulation provides 3,600 µg RE, more than double Singapore's maximum. The reformulation that clears Australia's limit (≤3,000 µg RE) still needs to go further to clear Singapore's (≤1,500 µg RE).",
          citation: 'SG Health Supplements Overview — HS guidelines',
          citationUrl: 'https://www.hsa.gov.sg/health-supplements/overview',
        },
        {
          title: 'No pre-market registration required',
          status: 'No gate',
          statusTone: 'green',
          body:
            'Health supplements are not subject to pre-marketing licensing in Singapore — there is no application and no registration number. Once composition and label are compliant, the product can go to market.',
        },
      ],
    },
    marketRow: { label: 'Singapore (HSA)', risk: 'Low risk', completeness: 25, required: 'Required 2/8', missing: '6 missing' },
    taama: {
      blocking: '3 blocking',
      toAction: '5 to action',
      summary:
        ' 3 blocking items. The artwork is missing the local importer, lot number and expiry date, and presents a health supplement using food wording.',
    },
    findings: {
      formula: { detail: '9 permitted · 1 conditions', count: '10' },
      label: { title: 'Packaging & Label', detail: '10 fail · 14 pass · 1 not applicable', count: '10' },
    },
    needs: {
      count: '7',
      items: [
        { title: 'Importer or local distributor details absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Batch/lot number and expiry date absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Recommended daily dose not stated as a supplement dose', status: 'Blocking', category: 'Labelling' },
        { title: 'Food-style directions of use to be removed', status: 'To action', category: 'Claims' },
        { title: 'United States DSHEA disclaimer to be removed', status: 'To action', category: 'Claims' },
      ],
      showMore: 'Show 2 more',
    },
    recentlyUpdated: [
      { type: 'Product Formulation', filename: 'Orange_Immunity_Powder_Formulation.xlsx', date: '26 Jul' },
      { type: 'Label Artwork', filename: 'Orange_15ct_Carton_R4.pdf', date: '26 Jul' },
    ],
  },
}

const chocolateRecord: ProductRecordConfig = {
  recordName: 'Chocolate Powdered Drink (Hot or Cold)',
  readiness: 0,
  pills: { category: 'food', markets: '1 market', documents: '2 documents', date: '7 Jul 2026' },
  market: { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'FSANZ' },
  hasNip: true,
  overview: {
    classification: 'Non-TGA Regulated Food',
    riskTag: 'Low risk',
    riskTooltip: 'Pathway risk — conventional foods carry the lightest regulatory burden',
    timingPill: 'No TGA approval — go to market when documents are ready',
    categoryPill: 'Food category: Chocolate and cocoa products',
    whyThisClassification:
      'Chocolate Powdered Drink is a conventional beverage powder sold as a general grocery item without any therapeutic claims, making it a Non-TGA Regulated Food rather than a Listed Complementary Medicine (AUST L), which requires active therapeutic ingredients and health-maintenance claims.',
    whatToExpect:
      'Lightest pathway. The product is regulated as a conventional food under the FSANZ Food Standards Code, not by the TGA, so there is no application and no pre-market approval. You only need to assemble standard food-safety and labelling documents; once they are in order the product can go to market. Typically days to a few weeks of internal preparation.',
    pathToMarket: {
      subtitle: 'How Chocolate Powdered Drink reaches shelf in Australia.',
      steps: [
        {
          title: 'No pre-market application required',
          status: 'No gate',
          statusTone: 'green',
          body:
            'Regulated as a conventional food under the FSANZ Food Standards Code, not by the TGA — no application, no pre-market approval.',
        },
        {
          title: 'Complete food-safety and labelling documentation',
          status: 'In progress',
          statusTone: 'amber',
          body:
            "The Nutrition Information Panel is complete. The blocker here isn't market entry — it's the NPSC score of 15 against a pass threshold of 4.0, which blocks all 37 health claims the composition would otherwise support until the product is reformulated.",
          citation: 'Nutrient Profiling Scoring Criterion',
          citationUrl:
            'https://www.foodstandards.gov.au/business/labelling/nutrition-health-and-related-claims/nutrient-profiling-scoring-criterion',
        },
      ],
    },
    marketRow: { label: 'Australia (FSANZ)', risk: 'Low risk', completeness: 16, required: 'Required 2/12', missing: '10 missing' },
    taama: {
      blocking: '12 blocking',
      toAction: '5 to action',
      summary: '12 blocking items holding back registration. Highest risk market: AU.',
    },
    findings: {
      formula: { detail: '4 food ingredient', count: '7' },
      label: { title: 'Label', detail: '34 missing', count: '34' },
    },
    needs: {
      count: '17',
      items: [
        { title: 'Claims Review missing', status: 'Blocking', category: 'Documentation' },
        { title: 'Finished Product CoA missing', status: 'Blocking', category: 'Documentation' },
        { title: 'GMP Certification missing', status: 'Blocking', category: 'Documentation' },
      ],
      showMore: 'Show 14 more',
    },
    recentlyUpdated: [
      { type: 'Product Formulation', filename: 'Chocolate_Powdered_Drink_Formulation.xlsx', date: '7 Jul' },
      { type: 'Label Artwork', filename: 'Chocolate_Powdered_Drink_Artwork.pdf', date: '7 Jul' },
    ],
  },
}

const cardComplexRecord: ProductRecordConfig = {
  recordName: 'Veno Complex Capsules',
  readiness: 24,
  pills: { category: 'capsules · supplement', markets: '1 market', documents: '1 document', date: '17 Aug 2026' },
  supportsCompare: false,
  market: { code: 'IT', flag: '🇮🇹', name: 'Italy', authority: '' },
  hasNip: false,
  overview: {
    classification: 'Food Supplement (Integratore Alimentare)',
    riskTag: 'Provisional',
    riskTone: 'warning',
    riskTooltip: 'Classification is provisional — re-check triggers apply, see below',
    timingPill: 'Notification only — no pre-market approval',
    categoryPill: 'Route: oral · Capsule',
    whyThisClassification:
      'Under EU and Italian law, this formulation fits the food supplement definition under D.Lgs. 169/2004, Art. 2: it is pre-dosed, concentrated, and intended to supplement the normal diet. The key question is supplement vs. medicine. Presentation is currently acceptable with no therapeutic claims, while dose is the main risk, particularly for aescin and Melilotus, which are active ingredients in registered EU medicines at comparable doses.',
    whatToExpect:
      'This is a provisional classification. Re-check once claims are drafted and if horse chestnut or sweet clover doses increase. If both remain within the relevant limits, the product should follow the food supplement notification route rather than medicine authorisation.',
    pathToMarket: {
      subtitle: 'How Veno Complex Capsules reaches shelf — EU-wide, then Italy specifically.',
      steps: [
        {
          title: 'EU: notification requirements vary by Member State',
          status: 'Confirm per market',
          statusTone: 'amber',
          body:
            'There is no single EU-wide pre-market authorisation for food supplements. Article 10 of Directive 2002/46/EC lets each Member State independently require notification, and requirements vary market by market. A 2026 ruling struck down notification at the level of individual shipments or consignments, but left per-market, per-product notification intact.',
          note: 'Confirm notification status for each EU market beyond Italy.',
          citation: 'Directive 2002/46/EC, Art. 10',
          citationUrl: 'https://eur-lex.europa.eu/eli/dir/2002/46/oj/eng',
        },
        {
          title: 'Italy: mandatory electronic notification before first sale',
          status: 'Not yet filed',
          statusTone: 'red',
          body:
            "Italy requires notification through the Ministry's 'Alimenti Soggetti a Notifica' system before market placement. This isn't an approval — conformity remains the operating food business's own responsibility — but the Ministry can flag non-conformities and block sale. EU-origin products with a compliant label can generally market from the point of notification; third-country-origin products wait out a 90-day window. If the Ministry objects, there's a 30-day window to remediate.",
          note: 'File once the label is finalised — see Product Dossier, R1.',
          citation: 'D.Lgs. 169/2004',
          citationUrl: 'https://www.gazzettaufficiale.it/eli/id/2004/07/15/004G0201/sg',
        },
      ],
    },
    marketRow: {
      label: 'Italy',
      risk: 'Medium risk',
      riskTone: 'warning',
      riskTooltip: 'Pathway risk — classification is provisional',
      completeness: 0,
      required: 'Required 0/8',
      missing: '8 missing',
    },
    taama: {
      blocking: '8 blocking',
      toAction: '12 to action',
      readiness: '24',
      summary:
        "8 blocking items across the formula and required documentation. Three ingredient issues are the live compliance risks — vitamin E dose, an unconfirmed botanical species, and horse chestnut's aescin dose — and the label itself hasn't been finalised yet.",
    },
    findings: {
      formula: { detail: '6 permitted · 4 with conditions · 6 verification required · 2 exceeds limit · 1 not permitted', count: '12' },
      claims: { detail: '1 permitted · 2 permitted with conditions', count: '3' },
      label: { title: 'Packaging & Label', detail: 'Not yet assessed', notAssessed: true },
    },
    needs: {
      count: '20',
      items: [
        { title: 'Confirm vitamin E dose and reduce if over 60 mg/day', status: 'Blocking', category: 'Formula' },
        { title: 'Confirm species and plant part for the witch hazel extract', status: 'Blocking', category: 'Formula' },
        { title: 'Reduce horse chestnut dose to bring aescin under 75 mg/day', status: 'Blocking', category: 'Formula' },
        { title: 'Novel Food technical file for nattokinase', status: 'Blocking', category: 'Documentation' },
        { title: 'Supplier documentation bundle outstanding', status: 'Blocking', category: 'Documentation' },
      ],
      showMore: 'Show 15 more',
    },
    recentlyUpdated: [{ type: '7559 Veno Complex Capsules - ohne Weißdorn - V3', filename: 'Source review', date: '17 Aug 2026' }],
  },
}

const adultSnacksRecord: ProductRecordConfig = {
  recordName: 'Cheese Snack',
  readiness: 32,
  pills: { category: 'snacks · crackers', markets: '1 market', documents: '1 document', date: '17 Aug 2026' },
  market: { code: 'PH', flag: '🇵🇭', name: 'Philippines', authority: 'FDA' },
  hasNip: false,
  showNip: true,
  overview: {
    classification: 'Conventional Food (Snack)',
    riskTag: 'Low risk',
    riskTooltip: 'Conventional food — no therapeutic pathway applies',
    timingPill: 'CPR required — Medium Risk tier',
    timingTone: 'warning',
    classificationNote: 'Risk tier unconfirmed — see note below',
    categoryPill: 'Retail · Multi-pack (4×30g sachets)',
    whyThisClassification:
      'Cheese Snack is a cheese-and-cracker snack pack with no therapeutic ingredients or medicinal claims, placing it in conventional food regulated by the Philippine FDA under AO 2014-0030.',
    whatToExpect:
      'This is a Medium Risk product under Dairy Products and Analogues, requiring a Certificate of Product Registration (CPR) rather than simple notification. The CPR is filed through the FDA eServices platform, with processing taking approximately 114 calendar days from a complete submission. One point remains to confirm: while two FDA annexes classify cheese as Medium Risk, a secondary industry source lists it as High Risk.',
    pathToMarket: {
      subtitle: 'CPR registration sequence for a Medium Risk imported dairy composite.',
      steps: [
        {
          title: 'Secure or hold an LTO as Importer or Trader',
          status: 'Precondition',
          statusTone: 'grey',
          body: 'A PH-based entity must hold a License to Operate before anything else in this sequence can start.',
        },
        {
          title: 'Confirm risk classification against the current Annex A/C',
          status: 'Unconfirmed',
          statusTone: 'amber',
          body: "Cheese's risk tier has one source disagreeing with two FDA-hosted annexes — resolve before proceeding.",
        },
        {
          title: 'Finalise formulation, claims, and PH-localised artwork',
          status: 'In progress',
          statusTone: 'amber',
          body:
            'Formula is clean; two claims (HALAL, 27% MORE PROTEIN) and the missing %PDRI panel still need resolving first — see Formula and Claims tabs.',
        },
        {
          title: 'File the CPR via eServices',
          status: 'Not started',
          statusTone: 'grey',
          body: "Submission platform is FDA Circular 2026-0002's eServices system, which replaced the old ePortal in June 2026.",
        },
        {
          title: 'CPR issued to the LTO holder, who becomes Market Authorization Holder (MAH)',
          status: 'Not started',
          statusTone: 'grey',
          body: 'Final step — registration issues to the entity holding the LTO.',
        },
      ],
      pendingCitations: ['AO 2014-0029', 'FDA Circular 2026-0002'],
    },
    marketRow: {
      label: 'Philippines',
      risk: 'Medium risk',
      riskTone: 'warning',
      riskTooltip: 'Label score 68/100 — 13 failing checks',
      completeness: 34,
      required: 'Score 34/100',
      missing: '13 fail · 3 warning',
    },
    taama: {
      blocking: '9 blocking',
      toAction: '16 to action',
      readiness: '32',
      summary:
        'Formula is clean and the label score is a reasonable 68/100, but zero of the eight required CPR filing documents are in hand yet, and the risk-tier classification itself needs confirming before filing. That combination — not the label — is what\u2019s holding readiness down.',
    },
    findings: {
      formula: { detail: '', count: '' },
      label: { title: 'Packaging & Label', detail: '13 fail · 3 warning · 1 inconclusive · 4 skip · 34 pass', count: '55' },
    },
    needs: {
      count: '25',
      items: [
        { title: 'Confirm risk tier — cheese may be High Risk, not Medium', status: 'Blocking', category: 'Classification' },
        { title: 'Secure or confirm LTO as Importer/Trader', status: 'Blocking', category: 'Documentation' },
        { title: 'Add %PDRI values throughout the nutrition panel', status: 'Blocking', category: 'Nutrition panel' },
        { title: "Remove or substantiate the '27% MORE PROTEIN' claim — absolute-difference test fails", status: 'Blocking', category: 'Claims' },
        { title: 'Add HALAL certifying-body logo or certificate number', status: 'Blocking', category: 'Claims' },
        { title: "Add the 'Based on PDRI 2015' mandatory statement", status: 'Blocking', category: 'Mandatory elements' },
        { title: 'Add a footnote explaining the caret (^) symbol on Protein', status: 'Blocking', category: 'Nutrition panel' },
      ],
      showMore: 'Show 11 more',
    },
    recentlyUpdated: [{ type: 'Label Assessment (v5 proof)', filename: 'Reviewed against the v5 proof artwork', date: '17 Aug 2026' }],
  },
}

const beautyGummiesRecord: ProductRecordConfig = {
  recordName: 'Beauty Vitamin Gummies',
  readiness: 22,
  pills: { category: 'supplements · gummies', markets: '1 market', documents: '0 documents', date: '22 Jun 2026' },
  market: { code: 'AU', flag: '🇦🇺', name: 'Australia', authority: 'TGA' },
  hasNip: false,
  overview: {
    classification: 'Listed Complementary Medicine (AUST L)',
    riskTag: 'Low risk',
    riskTooltip: 'Pathway risk — AUST L is the low-burden pathway',
    timingPill: 'Self-listed on the ARTG — often same day',
    categoryPill: 'Route: oral · Gummy',
    whyThisClassification:
      'Beauty Vitamin Gummies is a vitamin and mineral supplement in chewable gummy form making general health-maintenance claims, which fits the definition of a Listed Complementary Medicine (AUST L). It is classified here rather than as a conventional food because it is presented and marketed as a therapeutic supplement.',
    whatToExpect:
      'Fast, self-managed pathway — the sponsor self-certifies and the TGA performs an administrative check only. The current artwork is a United States label and requires substantial rework before listing: it carries no AUST L number, no Australian sponsor, no batch number, no expiry date, and no advisory statements. Vitamin A is present above the level at which an advisory statement is required.',
    pathToMarket: {
      subtitle: 'How Beauty Vitamin Gummies reaches shelf in Australia.',
      steps: [
        {
          title: 'Localise the artwork for the Australian market',
          status: 'Blocked',
          statusTone: 'red',
          body:
            'The current artwork is a United States label and needs substantial rework before listing: no AUST L number, no Australian sponsor, no batch number, no expiry date, and no advisory statements are present.',
          note:
            'Vitamin A is present above the level at which a mandatory advisory statement is required — this is a labelling fix, not a reformulation.',
        },
        {
          title: 'Self-list on the ARTG',
          status: 'Not started',
          statusTone: 'grey',
          body:
            'Once the artwork is Australia-ready, this is a fast, self-managed pathway — the sponsor self-certifies and the TGA performs an administrative check only. An AUST L number can usually be obtained as soon as listing data is entered.',
        },
      ],
    },
    marketRow: { label: 'Australia (TGA)', risk: 'Low risk', completeness: 0, required: 'Required 0/12', missing: '12 missing' },
    taama: {
      blocking: '5 blocking',
      toAction: '0 to action',
      readiness: '22',
      summary:
        'No formulation, claims, or label data has been added yet. The pathway itself is the low-burden AUST L listing, but the current United States artwork carries none of the Australian mandatory particulars.',
    },
    findings: {
      formula: { detail: '', count: '' },
      label: { title: 'Packaging & Label', detail: 'Not yet assessed', notAssessed: true },
    },
    needs: {
      count: '5',
      items: [
        { title: 'AUST L number absent from label', status: 'Blocking', category: 'Labelling' },
        { title: 'Australian sponsor name and address absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Batch/lot number and expiry date absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Vitamin A advisory statement absent', status: 'Blocking', category: 'Labelling' },
        { title: 'Label artwork not yet localised for Australia', status: 'Blocking', category: 'Documentation' },
      ],
      showMore: 'Show all 5',
    },
    recentlyUpdated: [{ type: 'Product record created', filename: 'No documents uploaded yet', date: '22 Jun 2026' }],
  },
}

const productRecords: Record<string, ProductRecordConfig> = {
  'beauty-vitamin-gummies': beautyGummiesRecord,
  'orange-immunity-powder': orangeRecord,
  'chocolate-powdered-drink': chocolateRecord,
  'card-complex-ven': cardComplexRecord,
  'adult-snacks-protein': adultSnacksRecord,
}

export function getProductRecord(id: string | undefined): ProductRecordConfig {
  return (id && productRecords[id]) || orangeRecord
}
